// ABOUTME: Compares what the carrier invoiced the fulfilment against what the fulfilment invoiced its customer.
// ABOUTME: Grid of the orders in the selected period; the summary cards above the tab view live in the parent.

import { Box, useTheme } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from 'react-i18next';
import { endOfMonth, format, startOfMonth, subMonths } from "date-fns";

import { tokens } from "../../../../../theme";
import ShDataGrid2 from "../../../../../components/theme/datagrid2/ShDataGrid2";
import SHQuickAction from "../../../../../components/theme/Modal/SHQuickAction";
import ShTextSkeleton from "../../../../../components/theme/skeleton/ShTextSkeleton";
import Text from "../../../../../components/theme/text/Text";
import SidepanelLoader from "../../../../../components/theme/sidepanel2/SidepanelLoader";
import { formatNumber, formatPrice } from "../../../../../utils/helpers";
import { useGetShippingCostOverviewQuery } from "../../../../../newapi/financial/invoiceSlice";
import { INVOICE_SHIPPING_COSTS } from "../../../../../components/global/DataGrid/Filter";
import ButtonGroup from "../../../../../components/theme/buttons/ButtonGroup";
import Shbutton from "../../../../../components/theme/buttons/Shbutton";
import MImportData from "../../../../../components/global/Modals/MImportData";
import { ImportStandAloneFileLogo, shippingCostShipperOptions } from "../../../../../utils/importColumnDefinitions";
import { selectUserPermission } from "../../../../../newapi/user/authentication/authSlice";
import { UserPrivileges } from "../../../../../utils/permissions";
import { useSelector } from "react-redux";
import UploadOutlinedIcon from '@mui/icons-material/UploadOutlined';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ShPillToggle from "../../../../../components/theme/buttons/ShPillToggle";
import { useGridFilter } from "../../../../../contexts/GridFilterContext";
import { updateFilter } from "../../../../../newapi/global/filter/gridFilterSlice";
import { useDispatch } from "react-redux";

const GridActions = (props) => {
    const { t } = useTranslation();

    if (!props.hasImportPermission) {
        return null;
    }

    return (
        <ButtonGroup version={2}>
            <Shbutton
                className={"group-btn"}
                color={"grey"}
                onClick={props.openImport}
                endIcon={<UploadOutlinedIcon/>}
                variant="contained"
            >
                {t('import')}
            </Shbutton>
        </ButtonGroup>
    );
};

// What the customer was invoiced against an order runs wider than its carriage: the rates
// ("tarieven") for the order lines picked, the material used, and so on. The comparison this
// overview exists for is carriage against carriage, so the rates are left out of the invoiced
// side unless asked for — which is what this switches.
//
// It writes into the grid filter rather than into state of its own, so the value reaches the
// request the same way every other filter does, and the totals cards above the grid follow it
// through filterObjectChanged. Rendered inside the grid header, so it reads the filter instance
// the header belongs to.
const RATE_OPTIONS = [
    { value: false, label: "only_shipping" },
    { value: true, label: "all" }
];

const RatesToggle = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { index, id } = useGridFilter();

    const includeOtherCosts = useSelector((state) => {
        const instance = state.gridFilter.viewInstances[index];
        return instance?.[id]?.filter?.includeOtherCosts === true;
    });

    return (
        <ShPillToggle
            options={RATE_OPTIONS}
            value={includeOtherCosts}
            onChange={(value) => dispatch(updateFilter({ index, id, filterKey: "includeOtherCosts", value }))}
            title={t("include_rates_explainer")}
        />
    );
};

const SpOrderDetails = SidepanelLoader(() => import("../../../../../components/global/Sidepanels/SpOrderDetails/SpOrderDetails"));
const SpRelationDetails = SidepanelLoader(() => import("../../../../../components/global/Sidepanels/SpRelationDetails/SpRelationDetails"));

let gridOptions = {
    export: true,
    disableCustomActionsOnPhone: true,
    isSidepanel: true,
    filter: [
        INVOICE_SHIPPING_COSTS.DATERANGE,
        INVOICE_SHIPPING_COSTS.SHIPPER,
        INVOICE_SHIPPING_COSTS.RELATION,
        INVOICE_SHIPPING_COSTS.COUNTRY,
        INVOICE_SHIPPING_COSTS.NEGATIVE_BALANCE,
        INVOICE_SHIPPING_COSTS.SALDO,
        INVOICE_SHIPPING_COSTS.WEIGHT
    ],
};

// Last calendar month, the period a carrier invoice normally covers.
const getDefaultDateRange = () => {
    const lastMonth = subMonths(new Date(), 1);
    return {
        startDate: format(startOfMonth(lastMonth), 'yyyy-MM-dd'),
        endDate: format(endOfMonth(lastMonth), 'yyyy-MM-dd')
    };
};

const toNumber = (value) => {
    const parsed = parseFloat(value);
    return isNaN(parsed) ? 0 : parsed;
};

// Carrier cost and invoiced revenue each live in two columns, a base and a surcharge. The
// rates the order was invoiced beside its carriage are a third column, counted only when the
// filter asks for them: the comparison this overview exists for is carriage against carriage.
const getPaid = (row) => toNumber(row.shippingCosts) + toNumber(row.adjustmentCharges);
const getInvoiced = (row, withOtherCosts) => toNumber(row.orderShippingCosts) + toNumber(row.orderAdjustmentCosts)
    + (withOtherCosts ? toNumber(row.orderOtherCosts) : 0);

// Both cost columns show the lines behind their total in a popover. The type is spelled out
// per line because a carrier like DHL folds its surcharges into the shipping charge, so the
// two are not obvious from the amounts alone.
const DETAIL_LABELS = {
    adjustment: "surcharge",
    other: "rate"
};

const CostBreakdown = ({ details, children, t, i18n }) => {
    if (!details || details.length === 0) {
        return children;
    }

    return (
        <SHQuickAction
            component={
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                    {details.map((detail, index) => (
                        <Box key={index} sx={{ display: "flex", justifyContent: "space-between", gap: 3 }}>
                            <Text light>
                                {`${t(DETAIL_LABELS[detail.type] ?? "shipping_cost")}: ${detail.amount}x ${detail.description}${detail.trackingNumber ? ` – ${detail.trackingNumber}` : ""}`}
                            </Text>
                            <Text>{formatPrice(i18n, detail.price)}</Text>
                        </Box>
                    ))}
                </Box>
            }
        >
            {({ anchorEl, onMouseEnter, onMouseLeave, onTouchStart }) => (
                <Box
                    aria-owns={anchorEl ? "simple-popover" : undefined}
                    aria-haspopup="true"
                    onMouseEnter={onMouseEnter}
                    onMouseLeave={onMouseLeave}
                    onTouchStart={onTouchStart}
                    sx={{ height: 1, display: "flex", alignItems: "center", cursor: "pointer" }}
                >
                    {children}
                </Box>
            )}
        </SHQuickAction>
    );
};

const ShippingCostOverview = (props) => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const ref = useRef();
    const [config, setConfig] = useState({
        date: getDefaultDateRange()
    });

    // The totals cards track what the shared grid filter is set to. ShDataGrid2 reports every
    // filter change through filterObjectChanged, so the cards stay in step with the grid — the
    // period and the carrier alike.
    const [cardFilter, setCardFilter] = useState({ date: config.date, shipperId: null, includeOtherCosts: false });

    const handleFilterChanged = (filterObject) => {
        const filter = filterObject?.filter;
        const date = filter?.date;
        const shipperId = parseInt(filter?.shipperId) > 0 ? parseInt(filter.shipperId) : null;
        const includeOtherCosts = filter?.includeOtherCosts === true;

        setCardFilter(prev => {
            // The grid reports its filter on changes that leave these three untouched as well.
            // Returning the previous object then keeps the cards from refetching, and keeps the
            // parent from re-rendering the tab underneath them.
            const nextDate = (date?.startDate && date?.endDate) ? date : prev.date;
            const sameDate = prev.date?.startDate === nextDate?.startDate
                && prev.date?.endDate === nextDate?.endDate;

            if (sameDate && prev.shipperId === shipperId && prev.includeOtherCosts === includeOtherCosts) {
                return prev;
            }

            return { date: nextDate, shipperId, includeOtherCosts };
        });
    };

    const dateRange = cardFilter.date;

    // The rates count towards the invoiced side only while the filter asks for them, so the
    // columns below read the same flag the grid and the cards were built with.
    const withOtherCosts = cardFilter.includeOtherCosts === true;

    const hasImportPermission = useSelector((state) =>
        selectUserPermission(state, [UserPrivileges.IMPORT_MANAGE])
    );

    const [importModalOpen, setImportModalOpen] = useState(false);

    const [orderId, setOrderId] = useState();
    const [isOrderExtended, setIsOrderExtended] = useState(false);
    const [spOrderLoading, setSpOrderLoading] = useState();

    const [relationId, setRelationId] = useState();
    const [isRelationExtended, setIsRelationExtended] = useState(false);
    const [spRelationLoading, setSpRelationLoading] = useState();

    // The totals cards live above the tab view, in the parent. Report the active filter up so
    // it can drive them, and clear it when leaving this tab so they only show here.
    useEffect(() => {
        props.onFilterChange?.(cardFilter);
        return () => props.onFilterChange?.(null);
    }, [cardFilter]);

    // The grid header spells out the period the rows below it cover.
    const gridTitle = t('shipping_cost_overview_from_to', {
        startDate: new Date(dateRange.startDate).toLocaleDateString(i18n.language),
        endDate: new Date(dateRange.endDate).toLocaleDateString(i18n.language),
    });

    useEffect(() => {
        props.replaceView({
            ...props.config,
            gridOptions: gridOptions,
            mobileActions: <GridActions hasImportPermission={hasImportPermission} openImport={() => setImportModalOpen(true)}/>,
        });

        return () => {};
    }, []);

    const getOrder = (params) => {
        if (!params.row.order?.id) {
            return;
        }
        setOrderId(params.row.order.id);
        if (!spOrderLoading) {
            setSpOrderLoading(true);
        }
        setIsOrderExtended(true);
    };

    const getRelation = (params) => {
        if (!params.row.order?.relation?.id) {
            return;
        }
        setRelationId(params.row.order.relation.id);
        if (!spRelationLoading) {
            setSpRelationLoading(true);
        }
        setIsRelationExtended(true);
    };

    // A negative saldo means the carrier charged more than the customer was invoiced, so it
    // is the number a fulfilment wants to spot at a glance.
    const saldoColor = (saldo) => saldo < 0 ? colors.red[400] : colors.green[400];

    const columns = [
        {
            field: "shipmentDate",
            headerName: t("date"),
            flex: 1,
            renderCell: (params) => {
                if (params.row.skeleton) {
                    return <ShTextSkeleton/>;
                }
                return (
                    <Text>
                        {params.row.shipmentDate ? new Date(params.row.shipmentDate).toLocaleDateString(i18n.language) : "-"}
                    </Text>
                );
            }
        },
        {
            field: "relationName",
            headerName: t("relation"),
            flex: 1,
            sortable: false,
            renderCell: (params) => {
                if (params.row.skeleton) {
                    return <ShTextSkeleton/>;
                }
                return (
                    <Box onClick={() => getRelation(params)} sx={{ cursor: "pointer" }}>
                        <Text>{params.row.order?.relation?.name ?? "-"}</Text>
                    </Box>
                );
            }
        },
        {
            field: "orderBarcode",
            headerName: t("order"),
            flex: 1,
            sortable: false,
            renderCell: (params) => {
                if (params.row.skeleton) {
                    return <ShTextSkeleton/>;
                }
                const handleClick = (event) => {
                    event.stopPropagation(); // Prevent click from propagating to the row
                    getOrder(params);
                };
                return (
                    <Box sx={{ height: 1, display: "flex", alignItems: "center" }} onClick={handleClick}>
                        <Text bold clickable={true} variant={theme.palette.mode === "dark" ? "blue" : "primary"}>
                            {params.row.order?.barcode ?? "-"}
                        </Text>
                    </Box>
                );
            }
        },
        {
            field: "shipperName",
            headerName: t("shipper"),
            flex: 2,
            sortable: false,
            renderCell: (params) => {
                if (params.row.skeleton) {
                    return <ShTextSkeleton/>;
                }

                // Carrier and profile together, since one carrier can be contracted several
                // ways and the rate depends on which.
                const shipper = params.row.shipper?.name;
                const profile = params.row.shipperProfile?.name;

                if (!shipper) {
                    return <Text>-</Text>;
                }

                return <Text>{profile ? `${shipper} - ${profile}` : shipper}</Text>;
            }
        },
        {
            field: "deliveryCountry",
            headerName: t("country"),
            flex: 0.5,
            sortable: false,
            renderCell: (params) => {
                if (params.row.skeleton) {
                    return <ShTextSkeleton/>;
                }
                return <Text>{params.row.order?.deliveryCountry ?? "-"}</Text>;
            }
        },
        {
            field: "volume",
            headerName: `${t("volume")} (L)`,
            flex: 0.5,
            sortable: false,
            renderCell: (params) => {
                if (params.row.skeleton) {
                    return <ShTextSkeleton/>;
                }
                // Stored as a decimal but only ever whole litres.
                return <Text>{formatNumber(i18n, Math.round(toNumber(params.row.order?.volume)))}</Text>;
            }
        },
        {
            field: "totalWeight",
            headerName: `${t("weight")} (g)`,
            flex: 0.5,
            sortable: false,
            renderCell: (params) => {
                if (params.row.skeleton) {
                    return <ShTextSkeleton/>;
                }
                // Same: a decimal column holding whole grams.
                return <Text>{formatNumber(i18n, Math.round(toNumber(params.row.order?.totalWeight)))}</Text>;
            }
        },
        {
            field: "amountOfShipments",
            headerName: t("shipments"),
            flex: 0.5,
            renderCell: (params) => {
                if (params.row.skeleton) {
                    return <ShTextSkeleton/>;
                }
                return <Text>{params.row.amountOfShipments}</Text>;
            }
        },
        {
            field: "orderShippingCosts",
            headerName: t("invoiced"),
            flex: 1,
            renderCell: (params) => {
                if (params.row.skeleton) {
                    return <ShTextSkeleton/>;
                }
                if (params.row.orderShippingCosts === null) {
                    return <Text light>{t("not_invoiced_yet")}</Text>;
                }

                // The lines the customer invoice was built from, so the two sides can be read
                // against each other without leaving the row. The rate lines are listed only
                // when they are counted, so the breakdown always adds up to the total beside it.
                const details = withOtherCosts
                    ? params.row.orderCostDetails
                    : (params.row.orderCostDetails ?? []).filter(detail => detail.type !== "other");

                return (
                    <CostBreakdown details={details} t={t} i18n={i18n}>
                        <Text>{formatPrice(i18n, getInvoiced(params.row, withOtherCosts))}</Text>
                    </CostBreakdown>
                );
            }
        },
        {
            field: "shippingCosts",
            headerName: t("paid_to_carrier"),
            flex: 1,
            renderCell: (params) => {
                if (params.row.skeleton) {
                    return <ShTextSkeleton/>;
                }

                const details = [...(params.row.shippingDetails ?? []), ...(params.row.adjustmentDetails ?? [])];

                return (
                    <CostBreakdown details={details} t={t} i18n={i18n}>
                        <Text>{formatPrice(i18n, getPaid(params.row))}</Text>
                    </CostBreakdown>
                );
            }
        },
        {
            // The same split as the totals card: a carrier like DHL folds its surcharges into
            // the shipping charge, so the two are worth reading apart per order as well.
            field: "adjustmentCharges",
            headerName: `${t("shipping_costs")} / ${t("surcharges")}`,
            flex: 1,
            renderCell: (params) => {
                if (params.row.skeleton) {
                    return <ShTextSkeleton/>;
                }

                return (
                    <Text>
                        {`${formatPrice(i18n, toNumber(params.row.shippingCosts))} / ${formatPrice(i18n, toNumber(params.row.adjustmentCharges))}`}
                    </Text>
                );
            }
        },
        {
            // Sortable, even though the saldo is no column: the backend orders on the same
            // expression the saldo filters run over.
            field: "saldo",
            headerName: t("saldo"),
            flex: 1,
            renderCell: (params) => {
                if (params.row.skeleton) {
                    return <ShTextSkeleton/>;
                }

                const saldo = getInvoiced(params.row, withOtherCosts) - getPaid(params.row);
                return (
                    <Text sx={{ color: `${saldoColor(saldo)} !important` }}>
                        {formatPrice(i18n, saldo)}
                    </Text>
                );
            }
        },
    ];

    return (
        <Box height={1} ref={ref} display={"flex"} flexDirection={"column"}>
            <Box sx={{ flex: 1, minHeight: 0 }}>
                <ShDataGrid2 key={theme.palette.mode}
                    content={useGetShippingCostOverviewQuery}
                    config={config}
                    title={gridTitle}
                    gridOptions={gridOptions}
                    sortModel={{field: "shipmentDate", type: "DESC"}}
                    columns={columns}
                    filterObjectChanged={handleFilterChanged}
                    leadingActions={<RatesToggle/>}
                    gridActions={<GridActions hasImportPermission={hasImportPermission} openImport={() => setImportModalOpen(true)}/>}
                >
                </ShDataGrid2>
            </Box>

            {/* The columns of a carrier invoice depend on the carrier, so the shipper is picked
                in the modal and travels along with the file. */}
            <MImportData
                open={importModalOpen}
                onOpen={() => setImportModalOpen(true)}
                onClose={() => setImportModalOpen(false)}
                handleClose={() => setImportModalOpen(false)}

                logo={<ImportStandAloneFileLogo Icon={LocalShippingIcon} />}
                title={t("shipping_cost_overview")}
                fileType={"verzendkosten"}
                notice={t("shipping_cost_import_notice")}
                importSettings={[
                    {
                        name: "shipperType",
                        label: t("shipper"),
                        type: "list",
                        defaultValue: "postnl",
                        options: shippingCostShipperOptions,
                    }
                ]}
            />

            {spOrderLoading &&
                <SpOrderDetails
                    orderId={orderId}
                    isExtended={isOrderExtended}
                    setIsExtended={setIsOrderExtended}
                />
            }

            {spRelationLoading &&
                <SpRelationDetails
                    relationId={relationId}
                    isExtended={isRelationExtended}
                    setIsExtended={setIsRelationExtended}
                />
            }
        </Box>
    );
};

export default ShippingCostOverview;
